// Left panel: groups list + group CRUD entry points (PRD 08 §2.2 #1, #2, #10).
// The +추가 button opens the form in create mode; per-card Edit/Delete buttons
// open the form in edit mode / delete confirm. Form panel and delete confirm
// are mounted here because they only operate on group-scoped state.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FolderOpen, FolderTree, Plus } from 'lucide-react';

import { useCan } from '@/features/roles/hooks/use-can';

import { useDiseaseGroupsQuery } from '../hooks/use-disease-groups';
import { useDiseaseCodePageStore } from '../store/disease-code-page.store';
import type { DiseaseGroup } from '../types/disease-code.types';
import { DeleteDiseaseGroupConfirm } from './DeleteDiseaseGroupConfirm';
import { DiseaseGroupCard } from './DiseaseGroupCard';
import { DiseaseGroupFormPanel } from './DiseaseGroupFormPanel';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

function GroupListSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            background: 'var(--muted)',
            height: 56,
          }}
        />
      ))}
    </>
  );
}

function GroupListError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        fontSize: 'var(--font-size-xs)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('adminManagement.actions.retry')}
      </button>
    </div>
  );
}

function GroupListEmpty() {
  const { t } = useTranslation('admin');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--muted-foreground)',
        padding: '40px 20px',
        textAlign: 'center',
        gap: 8,
      }}
    >
      <FolderOpen size={34} />
      <div>{t('diseaseCodeManagement.groupPanel.empty')}</div>
    </div>
  );
}

export function DiseaseGroupListPanel() {
  const { t } = useTranslation('admin');
  const canWrite = useCan('METADATA', 'WRITE');

  const { data: groups, isLoading, error, refetch } = useDiseaseGroupsQuery();
  const selectedGroupId = useDiseaseCodePageStore((s) => s.selectedGroupId);
  const selectGroup = useDiseaseCodePageStore((s) => s.selectGroup);

  // Form state: null = closed; { mode, group? } = open in create or edit mode.
  // `formOpen` is held separately so the SlidePanel can play its slide-out
  // animation before the `target` resets to null.
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<DiseaseGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DiseaseGroup | null>(null);
  // SHCT-582 (QC re-test) — a real `disabled` rather than `aria-disabled`: §7.4 P-2 is 후속 동작
  // 없음, and only a real one is visible to the L2 guard that answers a bypass (P-3).
  const { denyProps } = useWriteDenyGuard(canWrite);

  const openCreate = () => {
    setFormTarget(null);
    setFormOpen(true);
  };
  const openEdit = (group: DiseaseGroup) => {
    setFormTarget(group);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    // Match medical-staff: defer clearing the target until the slide-out
    // animation finishes so the form doesn't flash empty mid-transition.
    window.setTimeout(() => setFormTarget(null), 300);
  };

  const count = groups?.length ?? 0;

  return (
    <>
      <div
        className="card w-80 min-w-[320px] max-[1099px]:w-auto max-[1099px]:min-w-0"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
            }}
          >
            <FolderTree size={16} />
            <span>{t('diseaseCodeManagement.groupPanel.title')}</span>
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--muted-foreground)',
              marginLeft: 'auto',
              marginRight: 8,
            }}
          >
            {groups ? t('diseaseCodeManagement.groupPanel.count', { count }) : ''}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={!canWrite}
            onClick={openCreate}
            {...denyProps}
          >
            <Plus size={14} />
            {t('diseaseCodeManagement.groupPanel.add')}
          </button>
        </div>
        <div
          style={{
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {isLoading ? (
            <GroupListSkeleton />
          ) : error ? (
            <GroupListError
              message={t('diseaseCodeManagement.toasts.loadGroupsError')}
              onRetry={() => void refetch()}
            />
          ) : !groups || groups.length === 0 ? (
            <GroupListEmpty />
          ) : (
            groups.map((g) => (
              <DiseaseGroupCard
                key={g.id}
                group={g}
                selected={g.id === selectedGroupId}
                onSelect={selectGroup}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
                canWrite={canWrite}
                codeCountLabel={t('diseaseCodeManagement.groupPanel.codeCount', {
                  count: g.codeCount,
                })}
              />
            ))
          )}
        </div>
      </div>

      <DiseaseGroupFormPanel open={formOpen} group={formTarget} onClose={closeForm} />
      <DeleteDiseaseGroupConfirm
        group={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}
